// DrawArtworkModal.js
import React, { useEffect, useRef, useContext } from 'react';
import { ColorContext } from '../../Context/ColorContext';
import BottomColorPalette from '../BottomColorPalette';
import { BackendContext } from '../../Context/BackendContext';
import { X } from 'lucide-react';
import { Divider, Spinner, Button, Switch } from "@heroui/react";

const CHUNK = 64; // must match backend

const DrawArtworkModal = ({
  artworkId,
  onCloseModal,
  onSave,               // (base64) => void
  forwardedRef,
}) => {
  const canvasRef = useRef(null);
  const worldCanvasRef = useRef(null);
  const worldCtxRef = useRef(null);

  // NEW: interaction state (zoom/pan/drag)
  const viewRef = useRef({
    zoom: 1,            // user zoom multiplier (on top of base layout scale)
    minZoom: 0.2,
    maxZoom: 8,
    panX: 0,            // additional CSS px pan offsets
    panY: 0,
    dragging: false,
    lastX: 0,
    lastY: 0,
  });

  // Small helper
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

  const { selectedColor } = useContext(ColorContext);
  const backendContext = useContext(BackendContext);

  const [isCallingBackend, setIsCallingBackend] = React.useState(false);
  const [artworkLoading, setArtworkLoading] = React.useState(true);
  const [artwork, setArtwork] = React.useState(null);
  const [showGuides, setShowGuides] = React.useState(true);

  const [isDirty, setIsDirty] = React.useState(false);
  const AUTOSAVE_KEY = React.useMemo(() => `artparcel:artwork:${artworkId}`, [artworkId]);
  const baselineRef = useRef(null); // stores the original world image (dataURL)
  const spaceDownRef = useRef(false);
  const paintingRef = useRef(false);          // are we currently painting (Space+LMB held)
  const lastBrushPosRef = useRef(null);       // {i,j} in bbox-local pixels (not world)
  const [brushSize, setBrushSize] = React.useState(1); // radius in pixels (1..8)

  const DRAG_THRESHOLD = 3;               // px before we consider it a drag
  const mouseDownRef = useRef(false);
  const startPosRef = useRef({ x: 0, y: 0 });
  const maybePaintOnMouseUpRef = useRef(false);

  // Shape / layout refs
  const bboxRef = useRef({ minCol: 1, maxCol: 1, minRow: 1, maxRow: 1, bboxW: 1, bboxH: 1 });
  const layoutRef = useRef({ scale: 1, dx: 0, dy: 0, cssW: 800, cssH: 800 }); // draw area in canvas coords
  const maskSetRef = useRef(new Set()); // "col,row" strings for allowed pixels

  // ===== Helpers =====
  const keyFor = (col, row) => `${col},${row}`;
  const chunkOrigin = (cx, cy) => ({ col1: cx * CHUNK + 1, row1: cy * CHUNK + 1 });

  const base64ToBytes = (b64) => {
    if (typeof Buffer !== 'undefined') return Uint8Array.from(Buffer.from(b64, 'base64'));
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i) & 0xff;
    return out;
  };
  const base64ToU32 = (b64) => {
    const bytes = base64ToBytes(b64);
    const buf = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(buf).set(bytes);
    return new Uint32Array(buf);
  };
  const rleDecode = (u32, total) => {
    const bits = new Uint8Array(total);
    let curr = 0, idx = 0;
    for (let k = 0; k < u32.length; k++) {
      const n = u32[k] >>> 0;
      if (curr === 1) bits.fill(1, idx, idx + n);
      idx += n;
      curr ^= 1;
    }
    return bits;
  };


  // Size the visible canvas from the viewport & set DPR transform
  const resizeVisibleCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return { cssW: 800, cssH: 800 };

    const vw = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    const vh = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);

    // Leave room for modal header + controls so the modal never needs to scroll
    const CHROME_RESERVE = 160; // header, buttons, padding
    const maxByWidth  = Math.floor(vw * 0.8);                 // never take full width
    const maxByHeight = Math.floor(vh * 0.92 - CHROME_RESERVE);
    const side = Math.max(160, Math.min(maxByWidth, maxByHeight)); // square side

    const cssW = side;
    const cssH = side;

    const dpr = window.devicePixelRatio || 1;
    canvas.width  = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    canvas.style.width  = `${cssW}px`;
    canvas.style.height = `${cssH}px`;

    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // draw in CSS px

    return { cssW, cssH };
  };

  const computeLayout = (bboxW, bboxH, cssW, cssH) => {
    const margin = 8; // px
    const usableW = Math.max(1, cssW - margin * 2);
    const usableH = Math.max(1, cssH - margin * 2);
    const scale = Math.max(1, Math.floor(Math.min(usableW / bboxW, usableH / bboxH)));
    const drawW = bboxW * scale;
    const drawH = bboxH * scale;
    const dx = Math.floor((cssW - drawW) / 2);
    const dy = Math.floor((cssH - drawH) / 2);
    return { scale, dx, dy, cssW, cssH };
  };

  const clearCanvas = (ctx, cssW, cssH) => ctx.clearRect(0, 0, cssW, cssH);

  // Convert a mouse event → bbox-local pixel (i, j); returns null if out of bounds or outside mask
  const eventToLocalCell = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;
    const { minCol, minRow, bboxW, bboxH } = bboxRef.current;
    const { dx, dy, scale: baseScale } = layoutRef.current;
    const { zoom, panX, panY } = viewRef.current;
    const finalScale = baseScale * zoom;
    const drawX = dx + panX;
    const drawY = dy + panY;
    const lx = cssX - drawX;
    const ly = cssY - drawY;
    if (lx < 0 || ly < 0) return null;
    const i = Math.floor(lx / finalScale);
    const j = Math.floor(ly / finalScale);
    if (i < 0 || j < 0 || i >= bboxW || j >= bboxH) return null;
    const col = minCol + i;
    const row = minRow + j;
    if (!maskSetRef.current.has(`${col},${row}`)) return null;
    return { i, j };
  };

  // Low-level: stamp a filled circle of radius r at (i0,j0) onto world canvas
  const stampBrushAt = (i0, j0, r) => {
    const wctx = worldCtxRef.current;
    if (!wctx) return false;
    const { bboxW, bboxH, minCol, minRow } = bboxRef.current;

    // Clamp/round radius
    const R = Math.max(1, Math.floor(r));

    // Special case: r === 1 → single pixel only (no “plus”)
    if (R === 1) {
      if (i0 < 0 || j0 < 0 || i0 >= bboxW || j0 >= bboxH) return false;
      const col = minCol + i0;
      const row = minRow + j0;
      if (!maskSetRef.current.has(`${col},${row}`)) return false;
      wctx.fillRect(i0, j0, 1, 1);
      return true;
    }

    // Circle fill for R ≥ 2
    let wrote = false;
    const R2 = R * R;
    for (let dj = -R; dj <= R; dj++) {
      for (let di = -R; di <= R; di++) {
        if (di * di + dj * dj > R2) continue;
        const i = i0 + di, j = j0 + dj;
        if (i < 0 || j < 0 || i >= bboxW || j >= bboxH) continue;
        const col = minCol + i;
        const row = minRow + j;
        if (!maskSetRef.current.has(`${col},${row}`)) continue;
        wctx.fillRect(i, j, 1, 1);
        wrote = true;
      }
    }
    return wrote;
  };

  // Interpolate line from (i1,j1) → (i2,j2), stamping the brush along the path
  const strokeBrush = (i1, j1, i2, j2, r) => {
    const steps = Math.max(Math.abs(i2 - i1), Math.abs(j2 - j1));
    if (steps === 0) return stampBrushAt(i1, j1, r);
    let any = false;
    for (let s = 0; s <= steps; s++) {
      const i = Math.round(i1 + (i2 - i1) * (s / steps));
      const j = Math.round(j1 + (j2 - j1) * (s / steps));
      any = stampBrushAt(i, j, r) || any;
    }
    return any;
  };


  // ===== Fetch + initial render =====
  useEffect(() => {
    const fetchAndRender = async () => {
      if (!artworkId) return;
      setArtworkLoading(true);

      // Size the visible canvas from the viewport
      const { cssW, cssH } = resizeVisibleCanvas();
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      clearCanvas(ctx, cssW, cssH);

      try {
        // 1) fetch artwork metadata (for imageData, owner, etc.)
        const fetched = await backendContext.fetchArtwork(artworkId);
        setArtwork(fetched || null);

        // 2) fetch silhouette CHUNKS for the artwork
        const chunks = await backendContext.fetchArtworkSilhouetteChunks(artworkId); 
        // Expected row shape: { artworkId, page, chunkX, chunkY, width, height, rle, count }

        // 3) Decode chunks → build maskSet and exact bbox
        const maskSet = new Set(); // "col,row" (1-based)
        let minCol = Infinity, minRow = Infinity, maxCol = -Infinity, maxRow = -Infinity;

        for (const c of (chunks || [])) {
          const cx = c.chunkX ?? c.cx;
          const cy = c.chunkY ?? c.cy;
          const width  = c.width  ?? CHUNK;
          const height = c.height ?? CHUNK;
          const total = width * height;

          const bits = rleDecode(base64ToU32(c.rle), total);
          const { col1, row1 } = chunkOrigin(cx, cy);

          for (let j = 0; j < height; j++) {
            const rowOff = j * width;
            for (let i = 0; i < width; i++) {
              if (!bits[rowOff + i]) continue;
              const col = col1 + i;
              const row = row1 + j;
              maskSet.add(keyFor(col, row));
              if (col < minCol) minCol = col;
              if (row < minRow) minRow = row;
              if (col > maxCol) maxCol = col;
              if (row > maxRow) maxRow = row;
            }
          }
        }

        // 4) fallback if no mask bits (empty artwork)
        if (!Number.isFinite(minCol)) {
          minCol = maxCol = 1;
          minRow = maxRow = 1;
        }

        const bboxW = Math.max(1, maxCol - minCol + 1);
        const bboxH = Math.max(1, maxRow - minRow + 1);
        const bbox = { minCol, maxCol, minRow, maxRow, bboxW, bboxH };
        bboxRef.current = bbox;
        maskSetRef.current = maskSet;

        // 5) layout + draw
        layoutRef.current = computeLayout(bboxW, bboxH, cssW, cssH);

        clearCanvas(ctx, cssW, cssH);

        // --- Build world canvas at 1x1 per artwork pixel (bboxW x bboxH) ---
        const world = document.createElement('canvas');
        world.width = bboxW;
        world.height = bboxH;
        const wctx = world.getContext('2d', { willReadFrequently: true });
        wctx.clearRect(0, 0, bboxW, bboxH);

        // a) paint skeleton (mask) as white squares into world canvas
        if (maskSet.size > 0) {
          wctx.fillStyle = 'white';
          for (const k of maskSet) {
            const [col, row] = k.split(',').map(Number);
            const i = col - minCol;
            const j = row - minRow;
            wctx.fillRect(i, j, 1, 1);
          }
        }

        // b) draw existing image clipped to mask
        if (fetched?.imageData) {
          await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
              const originCol = fetched?.imageOriginColumn ?? minCol; // 1-based
              const originRow = fetched?.imageOriginRow   ?? minRow;
              const imgW = fetched?.imageWidth  ?? img.width;
              const imgH = fetched?.imageHeight ?? img.height;
              const dxLocal = originCol - minCol; // local to bbox
              const dyLocal = originRow - minRow;

              wctx.save();
              wctx.globalCompositeOperation = 'source-atop';
              wctx.imageSmoothingEnabled = false;
              wctx.drawImage(img, 0, 0, imgW, imgH, dxLocal, dyLocal, imgW, imgH);
              wctx.restore();
              resolve();
            };
            img.onerror = resolve;
            img.src = `data:image/png;base64,${fetched.imageData}`;
          });
        }

        // Stash in refs
        worldCanvasRef.current = world;
        worldCtxRef.current = wctx;

        // Snapshot pristine baseline BEFORE applying autosave
        try {
          baselineRef.current = world.toDataURL('image/png');
        } catch {}

        // --- Try restore from autosave (if dimensions match) ---
        try {
          const raw = localStorage.getItem(AUTOSAVE_KEY);
          if (raw) {
            const saved = JSON.parse(raw);
            if (saved?.png && saved?.bbox?.bboxW === bboxW && saved?.bbox?.bboxH === bboxH) {
              await new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                  wctx.clearRect(0, 0, bboxW, bboxH);
                  wctx.imageSmoothingEnabled = false;
                  wctx.drawImage(img, 0, 0);
                  resolve();
                };
                img.onerror = resolve;
                img.src = saved.png;
              });
              // restore view (optional)
              if (saved.view) {
                viewRef.current.zoom = saved.view.zoom ?? 1;
                viewRef.current.panX = saved.view.panX ?? 0;
                viewRef.current.panY = saved.view.panY ?? 0;
              }
              setShowGuides(saved.view?.showGuides ?? true);
              setIsDirty(true); // we have unsaved work in memory
            }
          }
        } catch {}

        // Reset view (centered); layoutRef already holds base scale/dx/dy.
        viewRef.current.zoom = 1;
        viewRef.current.panX = 0;
        viewRef.current.panY = 0;

        // Initial draw to visible canvas
        drawFrame();

        setArtworkLoading(false);
      } catch (err) {
        console.error('DrawArtworkModal: chunk-based render failed:', err);
        setArtworkLoading(false);
      }
    };

    fetchAndRender();
  }, [artworkId, backendContext]);

  // Warn if user tries to close/refresh the tab with unsaved edits
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = ''; // required for Chrome
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      if (e.code === 'Space' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault(); // avoid page scroll
        spaceDownRef.current = true;
        // Optional cursor cue:
        if (canvasRef.current) canvasRef.current.style.cursor = 'crosshair';
      }
    };
    const onKeyUp = (e) => {
      if (e.code === 'Space' || e.key === ' ' || e.key === 'Spacebar') {
        spaceDownRef.current = false;
        // If we were painting and Space released mid-drag, stop painting
        paintingRef.current = false;
        lastBrushPosRef.current = null;
        if (canvasRef.current && !viewRef.current.dragging) {
          canvasRef.current.style.cursor = 'crosshair';
        }
      }
    };
    window.addEventListener('keydown', onKeyDown, { passive: false });
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const drawGuides = (ctx, finalScale, drawX, drawY) => {
    const maskSet = maskSetRef.current;
    if (!maskSet || maskSet.size === 0) return;

    const { minCol, minRow, bboxW, bboxH } = bboxRef.current;

    // --- GRID (faint) ---
    ctx.save();
    ctx.beginPath();
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(255,255,255,0.12)'; // faint
    ctx.imageSmoothingEnabled = false;

    // vertical lines
    for (let i = 0; i <= bboxW; i++) {
      const x = Math.round(drawX + i * finalScale) + 0.5; // crisp 1px
      ctx.moveTo(x, Math.round(drawY) + 0.5);
      ctx.lineTo(x, Math.round(drawY + bboxH * finalScale) + 0.5);
    }
    // horizontal lines
    for (let j = 0; j <= bboxH; j++) {
      const y = Math.round(drawY + j * finalScale) + 0.5;
      ctx.moveTo(Math.round(drawX) + 0.5, y);
      ctx.lineTo(Math.round(drawX + bboxW * finalScale) + 0.5, y);
    }
    ctx.stroke();
    ctx.restore();

    // --- OUTLINE (thin white on outside edges only) ---
    ctx.save();
    ctx.beginPath();
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(255,255,255,0.9)'; // bright, thin
    const has = (i, j) => maskSet.has(`${minCol + i},${minRow + j}`);

    for (const key of maskSet) {
      const [col, row] = key.split(',').map(Number);
      const i = col - minCol;
      const j = row - minRow;
      const x0 = drawX + i * finalScale;
      const y0 = drawY + j * finalScale;
      const x1 = x0 + finalScale;
      const y1 = y0 + finalScale;

      // top edge (neighbor outside)
      if (j - 1 < 0 || !has(i, j - 1)) {
        ctx.moveTo(Math.round(x0) + 0.5, Math.round(y0) + 0.5);
        ctx.lineTo(Math.round(x1) + 0.5, Math.round(y0) + 0.5);
      }
      // bottom
      if (j + 1 >= bboxH || !has(i, j + 1)) {
        ctx.moveTo(Math.round(x0) + 0.5, Math.round(y1) + 0.5);
        ctx.lineTo(Math.round(x1) + 0.5, Math.round(y1) + 0.5);
      }
      // left
      if (i - 1 < 0 || !has(i - 1, j)) {
        ctx.moveTo(Math.round(x0) + 0.5, Math.round(y0) + 0.5);
        ctx.lineTo(Math.round(x0) + 0.5, Math.round(y1) + 0.5);
      }
      // right
      if (i + 1 >= bboxW || !has(i + 1, j)) {
        ctx.moveTo(Math.round(x1) + 0.5, Math.round(y0) + 0.5);
        ctx.lineTo(Math.round(x1) + 0.5, Math.round(y1) + 0.5);
      }
    }
    ctx.stroke();
    ctx.restore();
  };

  const drawFrame = () => {
    const canvas = canvasRef.current;
    const world = worldCanvasRef.current;
    if (!canvas || !world) return;

    const ctx = canvas.getContext('2d');
    const { cssW, cssH, dx, dy, scale: baseScale } = layoutRef.current;
    const { bboxW, bboxH } = bboxRef.current;
    const { zoom, panX, panY } = viewRef.current;

    clearCanvas(ctx, cssW, cssH);

    const finalScale = baseScale * zoom;
    const drawX = dx + panX;
    const drawY = dy + panY;

    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(
      world,
      0, 0, bboxW, bboxH,
      drawX, drawY, bboxW * finalScale, bboxH * finalScale
    );
    if (showGuides) {
      drawGuides(ctx, finalScale, drawX, drawY);
    }
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas || artworkLoading) return;

    const rect = canvas.getBoundingClientRect();
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;

    const { dx, dy, scale: baseScale } = layoutRef.current;
    const v = viewRef.current;

    const prevScale = baseScale * v.zoom;
    const worldX = (cssX - (dx + v.panX)) / prevScale;
    const worldY = (cssY - (dy + v.panY)) / prevScale;

    const factor = e.deltaY < 0 ? 1.2 : 0.8;
    const nextZoom = clamp(v.zoom * factor, v.minZoom, v.maxZoom);
    const newScale = baseScale * nextZoom;

    v.panX = cssX - dx - worldX * newScale;
    v.panY = cssY - dy - worldY * newScale;
    v.zoom = nextZoom;

    drawFrame();
    queueAutosave(1200);
  };

  const handleMouseDown = (e) => {
    if (artworkLoading || e.button !== 0) return; // LEFT only
    mouseDownRef.current = true;
    startPosRef.current = { x: e.clientX, y: e.clientY };
    maybePaintOnMouseUpRef.current = !spaceDownRef.current; // default click→paint unless Space-brush

    // If Space is held, we begin a brush stroke immediately
    if (spaceDownRef.current) {
      const cell = eventToLocalCell(e);
      if (!cell) return;
      const wctx = worldCtxRef.current;
      if (!wctx) return;
      wctx.imageSmoothingEnabled = false;
      wctx.fillStyle = selectedColor || '#000000';
      paintingRef.current = true;
      lastBrushPosRef.current = { i: cell.i, j: cell.j };
      const wrote = stampBrushAt(cell.i, cell.j, Math.max(1, brushSize));
      if (wrote) {
        setIsDirty(true);
        drawFrame();
      }
    } else {
      // We will pan only if mouse movement exceeds threshold.
      const v = viewRef.current;
      v.dragging = false;
      v.lastX = e.clientX;
      v.lastY = e.clientY;
      if (canvasRef.current) canvasRef.current.style.cursor = 'crosshair';
    }
  };

  const handleMouseMove = (e) => {
    const movedX = e.clientX - startPosRef.current.x;
    const movedY = e.clientY - startPosRef.current.y;
    const moved2 = movedX * movedX + movedY * movedY;

    const v = viewRef.current;
    // Brush move
    if (paintingRef.current && spaceDownRef.current) {
      const cell = eventToLocalCell(e);
      if (!cell || !lastBrushPosRef.current) return;
      const { i, j } = cell;
      const { i: li, j: lj } = lastBrushPosRef.current;
      const wrote = strokeBrush(li, lj, i, j, Math.max(1, brushSize));
      lastBrushPosRef.current = { i, j };
      if (wrote) {
        setIsDirty(true);
        drawFrame();           // show stroke instantly
      }
      return;
    }

    // No Space: decide between click (paint) vs pan (drag)
    if (!mouseDownRef.current) return;

    // If we haven't started dragging yet, check the threshold
    if (!v.dragging && moved2 >= DRAG_THRESHOLD * DRAG_THRESHOLD) {
      // Begin panning.
      v.dragging = true;
      maybePaintOnMouseUpRef.current = false; // once we pan, don't paint on mouseup
      if (canvasRef.current) canvasRef.current.style.cursor = 'grabbing';
      // Also reset last positions to avoid a jump
      v.lastX = e.clientX;
      v.lastY = e.clientY;
    }

    // Pan move
    if (!v.dragging) return;
      const dx = e.clientX - v.lastX;
      const dy = e.clientY - v.lastY;
      v.lastX = e.clientX;
      v.lastY = e.clientY;
      v.panX += dx;
      v.panY += dy;
      drawFrame();
      queueAutosave(1200);
    };

  const endDrag = () => {
    const v = viewRef.current;
    // Finish brush stroke
    if (paintingRef.current) {
      paintingRef.current = false;
      lastBrushPosRef.current = null;
      queueAutosave(800);
    }

    // If we never entered drag and no Space, treat it as a click-to-paint
    if (mouseDownRef.current && maybePaintOnMouseUpRef.current && !spaceDownRef.current) {
      // Paint a single pixel at the mouse-up position
      // (Using the last known event; attach the listener like below to capture 'e')
    }

    v.dragging = false;
    mouseDownRef.current = false;
    maybePaintOnMouseUpRef.current = false;
    if (canvasRef.current) canvasRef.current.style.cursor = 'crosshair';
  };

  // Buttons
  const zoomIn = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    // Zoom around center
    handleWheel({ preventDefault(){}, clientX: rect.left + rect.width/2, clientY: rect.top + rect.height/2, deltaY: -1 });
  };
  const zoomOut = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    handleWheel({ preventDefault(){}, clientX: rect.left + rect.width/2, clientY: rect.top + rect.height/2, deltaY: +1 });
  };
  const resetView = () => {
    const v = viewRef.current;
    v.zoom = 1; v.panX = 0; v.panY = 0;
    drawFrame();
  };

  const handleCanvasClick = (e) => {
    if (artworkLoading) return;
    if (!spaceDownRef.current) return; // only click-to-paint when Space is held
    const cell = eventToLocalCell(e);
    if (!cell) return;
    const wctx = worldCtxRef.current;
    if (!wctx) return;
    wctx.imageSmoothingEnabled = false;
    wctx.fillStyle = selectedColor || '#000000';
    const wrote = stampBrushAt(cell.i, cell.j, Math.max(1, brushSize));
    if (wrote) {
      setIsDirty(true);
      drawFrame();
      queueAutosave();
    }
  };

  // ---- Autosave (debounced) ----
  const autosaveTimerRef = useRef(null);
  const queueAutosave = (delayMs = 800) => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(doAutosave, delayMs);
  };

  const doAutosave = () => {
    const world = worldCanvasRef.current;
    if (!world) return;
    try {
      const payload = {
        ts: Date.now(),
        bbox: bboxRef.current,              // {minCol, minRow, bboxW, bboxH}
        view: {
          zoom: viewRef.current.zoom,
          panX: viewRef.current.panX,
          panY: viewRef.current.panY,
          showGuides,
        },
        png: world.toDataURL('image/png'),  // exact artwork pixels
      };
      localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(payload));
    } catch {}
  };

  const hasAutosave = React.useCallback(() => {
    try { return !!localStorage.getItem(AUTOSAVE_KEY); } catch { return false; }
  }, [AUTOSAVE_KEY]);

  const clearAutosave = React.useCallback(() => {
    try { localStorage.removeItem(AUTOSAVE_KEY); } catch {}
  }, [AUTOSAVE_KEY]);

  const restoreBaseline = React.useCallback(async () => {
    const world = worldCanvasRef.current;
    const wctx  = worldCtxRef.current;
    const { bboxW, bboxH } = bboxRef.current;
    if (!world || !wctx) return;

    // Prefer the pristine baseline; otherwise just clear to transparent
    const dataURL = baselineRef.current;
    wctx.clearRect(0, 0, bboxW, bboxH);
    if (dataURL) {
      await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => { wctx.drawImage(img, 0, 0); resolve(); };
        img.onerror = resolve;
        img.src = dataURL;
      });
    }
  }, []);

  const onSaveArtwork = async () => {
    if (!artwork) return;

    setIsCallingBackend(true);
    try {
      const fullCanvas = canvasRef.current;
      const { scale, dx, dy, cssW, cssH } = layoutRef.current;
      const { bboxW, bboxH, minCol, minRow } = bboxRef.current;

      // Extract exactly the drawable bbox area and scale it to bboxW×bboxH
      const t = document.createElement('canvas');
      t.width = bboxW;
      t.height = bboxH;
      const tctx = t.getContext('2d');
      tctx.clearRect(0, 0, bboxW, bboxH);

      const srcCtx = fullCanvas.getContext('2d');
      const dpr = window.devicePixelRatio || 1;
      const imageData = srcCtx.getImageData(0, 0, Math.round(cssW * dpr), Math.round(cssH * dpr));

      // Helper to read a pixel from the source canvas (accounting for DPR transform already set)
      const readPixel = (sx, sy) => {
        const ix = Math.max(0, Math.min(Math.floor(sx * dpr), imageData.width - 1));
        const iy = Math.max(0, Math.min(Math.floor(sy * dpr), imageData.height - 1));
        const idx = (iy * imageData.width + ix) * 4;
        return [
          imageData.data[idx],
          imageData.data[idx + 1],
          imageData.data[idx + 2],
          imageData.data[idx + 3],
        ];
      };

      // Write pixels
      for (let j = 0; j < bboxH; j++) {
        for (let i = 0; i < bboxW; i++) {
          const col = minCol + i;
          const row = minRow + j;
          // Only copy if the cell is part of the artwork mask; else keep transparent
          if (!maskSetRef.current.has(keyFor(col, row))) continue;

          // sample center of the scaled cell in the main canvas
          const cx = dx + i * scale + scale / 2;
          const cy = dy + j * scale + scale / 2;
          const [r, g, b, a] = readPixel(cx, cy);

          // draw 1×1 pixel in tempCanvas
          const img1 = tctx.createImageData(1, 1);
          img1.data[0] = r; img1.data[1] = g; img1.data[2] = b; img1.data[3] = a;
          tctx.putImageData(img1, i, j);
        }
      }

      const world = worldCanvasRef.current;
      const dataUrl = world.toDataURL('image/png');
      const base64 = dataUrl.split(',')[1];

      setIsCallingBackend(false);
      setIsDirty(false);
      localStorage.removeItem(AUTOSAVE_KEY);
      try { baselineRef.current = worldCanvasRef.current?.toDataURL('image/png'); } catch {}
      onSave?.(base64);
    } catch (err) {
      console.error('Saving artwork failed:', err);
      setIsCallingBackend(false);
    }
  };

  const onDiscardChanges = async () => {
    const should = window.confirm("Discard unsaved changes?");
    if (!should) return;

    clearAutosave();
    await restoreBaseline();
    setIsDirty(false);
    drawFrame();
  };

  const onCloseRequest = async () => {
    const hasDraft = isDirty || hasAutosave();
    if (hasDraft) {
      const ok = window.confirm("Discard unsaved changes and close?");
      if (!ok) return;
      clearAutosave();
      setIsDirty(false);
      // optional: revert in-memory world so if the modal stays mounted you don't see stale edits
      try {
        await restoreBaseline();
        drawFrame();
      } catch {}
    }
    onCloseModal?.();
  };

  // Reflow on window resize (no refetch; just resize & relayout)
  useEffect(() => {
    const onResize = () => {
      if (!canvasRef.current) return;
      const { cssW, cssH } = resizeVisibleCanvas();
      const { bboxW, bboxH } = bboxRef.current;
      layoutRef.current = computeLayout(bboxW, bboxH, cssW, cssH);
      drawFrame();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    drawFrame();
  }, [showGuides]);

  return (
    <>
      {isCallingBackend && (
        <div className="fixed inset-0 z-[1110] bg-black/70 flex items-center justify-center">
          <div className="bg-white rounded-lg p-4 flex items-center gap-2">
            <Spinner color="primary" size="lg" />
            <span className="text-black">saving artwork…</span>
          </div>
        </div>
      )}

      <div 
        ref={(forwardedRef)}
        className="fixed inset-0 bg-black/60 flex items-start justify-center z-[1050] pointer-events-none overflow-y-auto px-3 pt-4 pb-32">
        <div className="pointer-events-auto bg-slate-900/80 rounded-xl p-4 relative w-fit max-w-[96vw] max-h-[calc(100svh-9rem)] overflow-hidden mt-2 mb-auto">
          <button
            onClick={onCloseRequest}
            className="absolute top-4 right-4 text-zinc-400 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>

          <h2 className="mb-2 text-center font-bold">
            {artworkLoading ? 'Loading Artwork…' : `Editing Artwork #${artwork?.id ?? '—'}`}
          </h2>
          
          <div className="mt-3 flex items-start gap-4">
            <div className="overflow-hidden">
              <div className="relative">
                {artworkLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 z-10 rounded-lg">
                    <Spinner color="primary" size="lg" />
                  </div>
                )}
                <canvas
                  ref={canvasRef}
                  onWheel={handleWheel}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={(e) => {
                    // Click-to-paint if it wasn't a drag and Space isn't held
                    if (mouseDownRef.current && maybePaintOnMouseUpRef.current && !spaceDownRef.current) {
                      const cell = eventToLocalCell(e);
                      if (cell) {
                        const wctx = worldCtxRef.current;
                        if (wctx) {
                          wctx.imageSmoothingEnabled = false;
                          wctx.fillStyle = selectedColor || '#000000';
                          // single-pixel paint on click
                          const wrote = stampBrushAt(cell.i, cell.j, 1);
                          if (wrote) {
                            setIsDirty(true);
                            drawFrame();
                            queueAutosave();
                          }
                        }
                      }
                    }
                    endDrag();
                  }}
                  onMouseLeave={(e) => endDrag()}
                  onContextMenu={(e) => e.preventDefault()}
                  style={{ imageRendering: 'pixelated', border: '1px solid black', cursor: 'crosshair' }}
                />
              </div>
            </div>

            <div className="flex w-[180px] shrink-0 flex-col gap-3 rounded-2xl border border-slate-800 bg-slate-950/75 p-3">
              <div className="flex flex-col gap-2">
                <Button
                  size="sm"
                  className={`border-2 border-zinc-200 text-white rounded-2xl transition-colors ${
                    artworkLoading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-slate-800 hover:bg-indigo-700'
                  }`}
                  onPress={zoomOut}
                >
                  −
                </Button>
                <Button
                  size="sm"
                  className={`border-2 border-zinc-200 text-white rounded-2xl transition-colors ${
                    artworkLoading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-slate-800 hover:bg-indigo-700'
                  }`}
                  onPress={resetView}
                >
                  Reset
                </Button>
                <Button
                  size="sm"
                  className={`border-2 border-zinc-200 text-white rounded-2xl transition-colors ${
                    artworkLoading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-slate-800 hover:bg-indigo-700'
                  }`}
                  onPress={zoomIn}
                >
                  +
                </Button>
              </div>

              <div className="rounded-xl border border-slate-800 bg-slate-900/80 px-3 py-2">
                <Switch
                  size="sm"
                  isSelected={showGuides}
                  onValueChange={setShowGuides}
                >
                  Guides
                </Switch>
              </div>

              <Divider className="my-1" />

              <Button
                className={`w-full border-2 border-zinc-200 text-white rounded-2xl transition-colors ${
                  artworkLoading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-slate-800 hover:bg-indigo-700'
                }`}
                variant="flat"
                onPress={onDiscardChanges}
                disabled={!isDirty && !hasAutosave()}
              >
                Discard
              </Button>

              <Button
                onPress={onSaveArtwork}
                disabled={artworkLoading}
                className={`w-full border-2 border-zinc-200 text-white rounded-2xl transition-colors ${
                  artworkLoading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-slate-800 hover:bg-indigo-700'
                }`}
              >
                Save
              </Button>
            </div>
          </div>

        </div>
      </div>
      <BottomColorPalette isOpen zIndexClass="z-[1060]" />
    </>
  );
};

export default DrawArtworkModal;
